import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, FileDown, Loader2 } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { FeatureGate } from "@/components/UpgradeGate";
import { Button } from "@/components/ui/button";
import { DECK_KIND_LABEL, type DeckKind } from "@/lib/gamma";
import { pollDeckGeneration } from "@/lib/gamma.functions";

export const Route = createFileRoute("/_authenticated/reports/$id")({
  head: () => ({
    meta: [
      { title: "Slide report — MyPriorities" },
      {
        name: "description",
        content:
          "Track a Gamma slide deck as it is generated from your hub data, then open or download it.",
      },
      { property: "og:title", content: "Slide report — MyPriorities" },
      {
        property: "og:description",
        content: "Your generated deck, its outline and its download links.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: DeckRoute,
});

function DeckRoute() {
  return (
    <FeatureGate
      requires="pro"
      title="Slide reports"
      subtitle="Generate decks from your live hub data."
    >
      <DeckDetail />
    </FeatureGate>
  );
}

function DeckDetail() {
  const { id } = Route.useParams();

  const deck = useQuery({
    queryKey: ["deck", id],
    queryFn: () => pollDeckGeneration({ data: { id } }),
    refetchInterval: (query) => (query.state.data?.status === "pending" ? 5000 : false),
  });

  const row = deck.data;

  return (
    <AppShell
      title={row?.title ?? "Slide report"}
      subtitle={
        row ? (DECK_KIND_LABEL[row.kind as DeckKind] ?? row.kind) : "Generating from your hub data"
      }
      action={
        <Button variant="outline" asChild>
          <Link to="/reports">
            <ArrowLeft className="mr-1.5 size-4" /> All reports
          </Link>
        </Button>
      }
    >
      {deck.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !row ? (
        <p className="text-sm text-muted-foreground">That report no longer exists.</p>
      ) : (
        <div className="space-y-5">
          <section className="rounded-xl border border-border bg-card p-5">
            {row.status === "pending" ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" /> Gamma is designing your slides. This
                page updates itself.
              </p>
            ) : row.status === "failed" ? (
              <p className="text-sm text-destructive">
                {row.error ?? "Gamma couldn't build this deck."}
              </p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {row.deck_url ? (
                  <Button asChild>
                    <a href={row.deck_url} target="_blank" rel="noreferrer">
                      <ExternalLink className="mr-1.5 size-4" /> Open in Gamma
                    </a>
                  </Button>
                ) : null}
                {row.export_url ? (
                  <Button variant="outline" asChild>
                    <a href={row.export_url} target="_blank" rel="noreferrer">
                      <FileDown className="mr-1.5 size-4" /> Download file
                    </a>
                  </Button>
                ) : null}
              </div>
            )}
            <p className="mt-3 text-xs text-muted-foreground">
              Created {new Date(row.created_at).toLocaleString("en-GB")}
              {row.credits_used ? ` · ${row.credits_used} Gamma credits` : ""}
            </p>
          </section>

          <section className="rounded-xl border border-border bg-card p-5">
            <h2 className="font-display text-lg font-semibold">The outline we sent</h2>
            <pre className="mt-3 max-h-[420px] overflow-auto whitespace-pre-wrap rounded-lg bg-muted p-3 text-xs">
              {row.brief}
            </pre>
          </section>
        </div>
      )}
    </AppShell>
  );
}
